import { Fragment, useEffect, useState } from 'react'
import { invitation } from '../data/invitation.js'
import styles from './GuestSnap.module.css'
import { SnapModal } from './SnapModal.js'

export interface GuestSnapProps {
  /** 업로드 창이 열리는 ISO 시각. null이면 게이트가 없다 — 항상 열려 있다. */
  openAt: string | null
  onNotify: (message: string) => void
}

/**
 * setTimeout의 지연은 32비트를 넘기면 넘쳐 즉시 발화한다. 예식이 몇 달 뒤면
 * 남은 시간이 그 한계를 우습게 넘으므로, 한 번에 이만큼씩만 재우고 다시 감는다.
 */
const MAX_TIMER_MS = 2_000_000_000

function isOpenNow(openAt: string | null): boolean {
  if (openAt === null) return true
  const at = new Date(openAt).getTime()
  // 파싱 실패(NaN)면 게이트가 없는 것으로 본다 — 잘못된 값 하나가 업로드를 막지 않는다.
  return !(at > Date.now())
}

/** 원본 data-screen-label="09 게스트 스냅" 블록. */
export function GuestSnap({ openAt, onNotify }: GuestSnapProps) {
  const [open, setOpen] = useState(false)
  const [openNow, setOpenNow] = useState(() => isOpenNow(openAt))

  // 게이트가 열리는 순간을 스스로 기다린다. 예전에는 App의 60초 D-day 시계가
  // 리렌더를 일으켜 우연히 풀렸다 — 그 시계를 지우면 버튼은 새로고침 전까지
  // 비활성인 채로 남는다. 우연이 아니라 의도로 열리게 한다.
  useEffect(() => {
    if (openNow) return
    let timer: ReturnType<typeof setTimeout>
    const tick = () => {
      if (isOpenNow(openAt)) {
        setOpenNow(true)
        return
      }
      const remaining = new Date(openAt!).getTime() - Date.now()
      timer = setTimeout(tick, Math.min(remaining, MAX_TIMER_MS))
    }
    tick()
    return () => clearTimeout(timer)
  }, [openAt, openNow])

  const disabled = !openNow

  return (
    <div className={styles.wrap}>
      <div className={styles.divider}>
        <span className={styles.line} />
        <span className={`${styles.dot} ${styles.dotSmall}`} />
        <span className={`${styles.dot} ${styles.dotMid}`} />
        <span className={`${styles.dot} ${styles.dotSmall}`} />
        <span className={styles.line} />
      </div>
      <div className={styles.eyebrow}>guest snap</div>
      <h2 className={styles.title}>{invitation.snap.title}</h2>
      <p className={styles.body}>
        {invitation.snap.body.map((line, index) => (
          <Fragment key={line}>
            {index > 0 && <br />}
            {line}
          </Fragment>
        ))}
      </p>
      <button type="button" className={styles.button} disabled={disabled} onClick={() => setOpen(true)}>
        사진 올리기
      </button>
      <div className={styles.hint}>{invitation.snap.hint}</div>
      {open && <SnapModal onClose={() => setOpen(false)} onNotify={onNotify} />}
    </div>
  )
}
